import { ConnectedPosition, FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import { ComponentRef } from '@angular/core';
import { Subscription } from 'rxjs';

import { getTooltipSide, TOOLTIP_OFFSET } from './tooltip-positions';

import { TooltipComponent } from './tooltip.component';

export interface TooltipConfig {
  readonly content: string;
  readonly html: boolean;
  readonly positions: readonly ConnectedPosition[];
  readonly widthAuto: boolean;
}

const PANE_CLASS = 'ideals-tooltip-pane';

export class TooltipPresenter {
  readonly #element: HTMLElement;
  readonly #overlay: Overlay;

  #componentRef?: ComponentRef<TooltipComponent>;
  #overlayRef?: OverlayRef;
  #positionChanges?: Subscription;

  constructor(element: HTMLElement, overlay: Overlay) {
    this.#element = element;
    this.#overlay = overlay;
  }

  get isOpen(): boolean {
    return this.#overlayRef !== undefined;
  }

  hide(): void {
    this.#positionChanges?.unsubscribe();
    this.#overlayRef?.dispose();
    this.#positionChanges = undefined;
    this.#overlayRef = undefined;
    this.#componentRef = undefined;
  }

  show({ content, html, positions, widthAuto }: TooltipConfig): void {
    if (this.isOpen) {
      this.update(content);

      return;
    }

    const positionStrategy = this.#createPositionStrategy(positions);

    this.#overlayRef = this.#overlay.create({
      panelClass: PANE_CLASS,
      positionStrategy,
      scrollStrategy: this.#overlay.scrollStrategies.noop(),
    });
    this.#componentRef = this.#overlayRef.attach(new ComponentPortal(TooltipComponent));
    this.#componentRef.setInput('html', html);
    this.#componentRef.setInput('widthAuto', widthAuto);
    this.#componentRef.setInput('side', getTooltipSide(positions[0]));
    this.update(content);

    this.#positionChanges = positionStrategy.positionChanges.subscribe(({ connectionPair }) => {
      this.#componentRef?.setInput('side', getTooltipSide(connectionPair));
      this.#componentRef?.changeDetectorRef.detectChanges();
    });
  }

  update(content: string): void {
    if (!this.#componentRef) {
      return;
    }

    this.#componentRef.setInput('content', content);
    this.#componentRef.changeDetectorRef.detectChanges();
    this.#overlayRef?.updatePosition();
  }

  #createPositionStrategy(positions: readonly ConnectedPosition[]): FlexibleConnectedPositionStrategy {
    return this.#overlay.position()
      .flexibleConnectedTo(this.#element)
      .withFlexibleDimensions(false)
      .withPositions([...positions])
      .withPush(false)
      .withViewportMargin(TOOLTIP_OFFSET);
  }
}
